import { useCallback, useEffect, useRef, useState } from '@wordpress/element';
import { __, sprintf } from '@wordpress/i18n';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { organizeFieldsByGroup } from '../utils/fieldTypes';
import { useMetaCleanup } from './useMetaCleanup';

export const usePropertyBuilder = (showToast) => {
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [activeField, setActiveField] = useState(null);
  const [isTabModalOpen, setIsTabModalOpen] = useState(false);
  const [isFieldModalOpen, setIsFieldModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [groupCollapsedStates, setGroupCollapsedStates] = useState({});
  const [shouldReload, setShouldReload] = useState(false);

  const {
    isCleaning,
    cleanupOrphanedMeta,
    resetAndCleanup,
    getOrphanedStats
  } = useMetaCleanup(null);

  useEffect(() => {
    if (shouldReload) {
      setShouldReload(false);
      window.location.reload();
    }
  }, [shouldReload]);

  const API_BASE = window.HavenlyticsData?.hvnlyNabApi?.url || null;
  const API_NONCE = window.HavenlyticsData?.hvnlyNabApi?.nonce || '';
  const SECTIONS_ENDPOINT = `${API_BASE}/pb-dnd-sections`;

  const AXIOS_CONFIG = {
    headers: { 
      'X-WP-Nonce': API_NONCE,
      'Content-Type': 'application/json'
    }
  };

  const tabsRef = useRef(tabs);
  const showToastRef = useRef(showToast);
  const groupCollapsedStatesRef = useRef(groupCollapsedStates);
  
  useEffect(() => {
    tabsRef.current = tabs;
    showToastRef.current = showToast;
    groupCollapsedStatesRef.current = groupCollapsedStates;
  }, [tabs, showToast, groupCollapsedStates]);

  const isRequiredTab = (tab) => {
    return tab?.required || tab?.title === 'Basic Info';
  };

  const toggleSidebar = useCallback((groupId, showInSidebar) => {
    setTabs(prevTabs => {
      return prevTabs.map(tab => {
        const hasGroup = tab.fields.some(field => field.group_id === groupId);
        
        if (hasGroup) {
          const updatedFields = tab.fields.map(field => {
            if (field.group_id === groupId) {
              return {
                ...field,
                show_in_sidebar: showInSidebar
              };
            }
            return field;
          });
          
          return { ...tab, fields: updatedFields };
        }
        return tab;
      });
    });
    
    showToast(
      'success',
      __('Sidebar Updated', 'havenlytics'),
      showInSidebar 
        ? __('Documents will show in sidebar widget', 'havenlytics') 
        : __('Documents hidden from sidebar widget', 'havenlytics')
    );
  }, [showToast]);

  const moveGroupUp = (groupId, groupIndex) => {
    if (groupIndex <= 0) return;
    
    setTabs(prevTabs => {
      return prevTabs.map(tab => {
        const groupFields = tab.fields.filter(field => field.group_id === groupId);
        if (groupFields.length === 0) return tab;
        
        const allFields = [...tab.fields];
        
        const groupFieldIndices = groupFields.map(f => 
          allFields.findIndex(field => field.id === f.id)
        ).filter(idx => idx !== -1);
        
        if (groupFieldIndices.length === 0) return tab;
        
        const firstGroupIndex = Math.min(...groupFieldIndices);
        
        if (firstGroupIndex === 0) return tab;
        
        const fieldAbove = allFields[firstGroupIndex - 1];
        
        const isFieldAbovePartOfGroup = fieldAbove && fieldAbove.group_id;
        
        if (isFieldAbovePartOfGroup) {
          const aboveGroupId = fieldAbove.group_id;
          const aboveGroupFields = allFields.filter(f => f.group_id === aboveGroupId);
          const aboveGroupIndices = aboveGroupFields.map(f => 
            allFields.findIndex(field => field.id === f.id)
          ).filter(idx => idx !== -1);
          
          const firstAboveIndex = Math.min(...aboveGroupIndices);
          const lastAboveIndex = Math.max(...aboveGroupIndices);
          const lastGroupIndex = Math.max(...groupFieldIndices);
          
          const newFields = [
            ...allFields.slice(0, firstAboveIndex),
            ...allFields.slice(firstGroupIndex, lastGroupIndex + 1),
            ...allFields.slice(firstAboveIndex, lastAboveIndex + 1),
            ...allFields.slice(lastGroupIndex + 1)
          ];
          
          const updatedFields = newFields.map((field, index) => ({
            ...field,
            order: index
          }));
          
          return { ...tab, fields: updatedFields };
        } else {
          const newFields = [...allFields];
          
          [newFields[firstGroupIndex - 1], newFields[firstGroupIndex]] = 
          [newFields[firstGroupIndex], newFields[firstGroupIndex - 1]];
          
          const updatedFields = newFields.map((field, index) => ({
            ...field,
            order: index
          }));
          
          return { ...tab, fields: updatedFields };
        }
      });
    });
    
    showToast('success', __('Group Moved', 'havenlytics'), __('Group moved up successfully', 'havenlytics'));
  };

  const moveGroupDown = (groupId, groupIndex) => {
    setTabs(prevTabs => {
      return prevTabs.map(tab => {
        const groupFields = tab.fields.filter(field => field.group_id === groupId);
        if (groupFields.length === 0) return tab;
        
        const allFields = [...tab.fields];
        
        const groupFieldIndices = groupFields.map(f => 
          allFields.findIndex(field => field.id === f.id)
        ).filter(idx => idx !== -1);
        
        if (groupFieldIndices.length === 0) return tab;
        
        const lastGroupIndex = Math.max(...groupFieldIndices);
        
        if (lastGroupIndex === allFields.length - 1) return tab;
        
        const fieldBelow = allFields[lastGroupIndex + 1];
        
        const isFieldBelowPartOfGroup = fieldBelow && fieldBelow.group_id;
        
        if (isFieldBelowPartOfGroup) {
          const belowGroupId = fieldBelow.group_id;
          const belowGroupFields = allFields.filter(f => f.group_id === belowGroupId);
          const belowGroupIndices = belowGroupFields.map(f => 
            allFields.findIndex(field => field.id === f.id)
          ).filter(idx => idx !== -1);
          
          const firstGroupIndex = Math.min(...groupFieldIndices);
          const firstBelowIndex = Math.min(...belowGroupIndices);
          const lastBelowIndex = Math.max(...belowGroupIndices);
          
          const newFields = [
            ...allFields.slice(0, firstGroupIndex),
            ...allFields.slice(firstBelowIndex, lastBelowIndex + 1),
            ...allFields.slice(firstGroupIndex, lastGroupIndex + 1),
            ...allFields.slice(lastBelowIndex + 1)
          ];
          
          const updatedFields = newFields.map((field, index) => ({
            ...field,
            order: index
          }));
          
          return { ...tab, fields: updatedFields };
        } else {
          const newFields = [...allFields];
          
          [newFields[lastGroupIndex], newFields[lastGroupIndex + 1]] = 
          [newFields[lastGroupIndex + 1], newFields[lastGroupIndex]];
          
          const updatedFields = newFields.map((field, index) => ({
            ...field,
            order: index
          }));
          
          return { ...tab, fields: updatedFields };
        }
      });
    });
    
    showToast('success', __('Group Moved', 'havenlytics'), __('Group moved down successfully', 'havenlytics'));
  };

  const toggleGroupCollapse = (groupId) => {
    setGroupCollapsedStates(prev => {
      const newState = !prev[groupId];
      
      setTabs(currentTabs => {
        return currentTabs.map(tab => {
          const updatedFields = tab.fields.map(field => {
            if (field.group_id === groupId) {
              return {
                ...field,
                group_collapsed: newState
              };
            }
            return field;
          });
          return { ...tab, fields: updatedFields };
        });
      });
      
      return {
        ...prev,
        [groupId]: newState
      };
    });
  };

  const initializeGroupCollapsedStates = useCallback((tabsData) => {
    const newGroupStates = {};
    
    tabsData.forEach(tab => {
      const organized = organizeFieldsByGroup(tab.fields || []);
      Object.keys(organized.groups).forEach(groupId => {
        const groupFields = organized.groups[groupId].fields;
        const hasCollapsedState = groupFields.some(f => f.group_collapsed !== undefined);
        
        if (hasCollapsedState) {
          newGroupStates[groupId] = groupFields[0].group_collapsed || false;
        } else {
          newGroupStates[groupId] = false;
        }
      });
    });
    
    setGroupCollapsedStates(newGroupStates);
  }, []);

  const createDefaultRequiredTabs = () => {
    return [
      {
        id: 'sec_basic_info',
        title: 'Basic Info',
        icon: 'fas fa-home',
        fields: [
          {
            id: '_hvnly_property_price',
            name: '_hvnly_property_price',
            type: 'price_label',
            label: 'Property Price',
            placeholder: 'Enter property price',
            required: true,
            locked: true,
            adminOnly: false,
            enabled: true,
            order: 0
          },
          {
            id: '_hvnly_property_reception_rooms',
            name: '_hvnly_property_reception_rooms',
            type: 'number',
            label: 'Property Reception Rooms',
            placeholder: 'Enter reception rooms',
            required: true,
            locked: true,
            adminOnly: false,
            enabled: true,
            order: 1
          },
          {
            id: '_hvnly_property_bedrooms',
            name: '_hvnly_property_bedrooms',
            type: 'number',
            label: 'Property Bedrooms',
            placeholder: 'Enter property bedrooms',
            required: true,
            locked: true,
            adminOnly: false,
            enabled: true,
            order: 2
          },
          {
            id: '_hvnly_property_bathrooms',
            name: '_hvnly_property_bathrooms',
            type: 'number',
            label: 'Property Bathrooms',
            placeholder: 'Enter property bathrooms',
            required: true,
            locked: true,
            adminOnly: false,
            enabled: true,
            order: 3
          },
          {
            id: '_hvnly_property_kitchens',
            name: '_hvnly_property_kitchens',
            type: 'number',
            label: 'Property Total Kitchens',
            placeholder: 'Enter property total kitchens',
            required: true,
            locked: true,
            adminOnly: false,
            enabled: true,
            order: 4
          },
          {
            id: '_hvnly_property_total_rooms',
            name: '_hvnly_property_total_rooms',
            type: 'number',
            label: 'Property Total Rooms',
            placeholder: 'Enter property total rooms',
            required: true,
            locked: true,
            adminOnly: false,
            enabled: true,
            order: 5
          },
          {
            id: '_hvnly_property_floors',
            name: '_hvnly_property_floors',
            type: 'number',
            label: 'Property Total Floors',
            placeholder: 'Enter property total floors',
            required: true,
            locked: true,
            adminOnly: false,
            enabled: true,
            order: 6
          },
          {
            id: '_hvnly_property_year_built',
            name: '_hvnly_property_year_built',
            type: 'number',
            label: 'Property Year Built',
            placeholder: 'Enter property year built',
            required: true,
            locked: true,
            adminOnly: false,
            enabled: true,
            order: 7
          },
          {
            id: '_hvnly_property_garage_sqft',
            name: '_hvnly_property_garage_sqft',
            type: 'number',
            label: 'Property Garage Square Footage',
            placeholder: 'Enter property garage square footage',
            required: true,
            locked: true,
            adminOnly: false,
            enabled: true,
            order: 8
          }
          
        ],
        collapsed: false,
        order: 0,
        required: true
      }
    ];
  };

  const filterOutAmenitiesSidebar = (data) => {
    if (!Array.isArray(data)) return data || [];
    
    return data.filter(item => {
      if (!item || typeof item !== 'object') return false;
      const title = item.title || item.hvnly__sectiontitle || '';
      return title !== 'Amenities Sidebar';
    });
  };

const mergeWithDefaultStructure = (apiData) => {
  if (!Array.isArray(apiData)) return createDefaultRequiredTabs();
  
  const filteredApiData = filterOutAmenitiesSidebar(apiData);
  const defaultTabs = createDefaultRequiredTabs();
  const mergedTabs = [...defaultTabs];
  
  filteredApiData.forEach(apiTab => {
    const isRequired = isRequiredTab(apiTab);
    
    if (isRequired) {
      const defaultTabIndex = mergedTabs.findIndex(t => t.title === apiTab.title);
      
      if (defaultTabIndex !== -1) {
        const mergedTab = { ...mergedTabs[defaultTabIndex] };
        
        if (Array.isArray(apiTab.fields)) {
          mergedTab.fields = mergedTab.fields.map(defaultField => {
            const apiField = apiTab.fields.find(f => f.id === defaultField.id);
            if (apiField) {
              // CRITICAL FIX: Force price_label type for price field
              const isPriceField = defaultField.id === '_hvnly_property_price';
              const finalType = isPriceField ? 'price_label' : (apiField.type || defaultField.type);
              
              return {
                ...defaultField,
                ...apiField,
                type: finalType,
                locked: defaultField.locked,
                required: defaultField.required,
                adminOnly: defaultField.adminOnly,
                enabled: apiField.enabled !== false
              };
            }
            return defaultField;
          });
        }
        
        mergedTabs[defaultTabIndex] = mergedTab;
      }
    } else {
      mergedTabs.push(apiTab);
    }
  });
  
  return enforceRequiredTabPositions(mergedTabs);
};

const enforceRequiredTabPositions = (tabsArray) => {
  if (!Array.isArray(tabsArray)) return [];
  
  const basicInfoTab = tabsArray.find(tab => tab?.title === 'Basic Info');
  const otherTabs = tabsArray.filter(tab => tab?.title !== 'Basic Info');

  const orderedTabs = [];
  if (basicInfoTab) orderedTabs.push(basicInfoTab);
  orderedTabs.push(...otherTabs);

  return orderedTabs.map((tab, index) => ({
    ...tab,
    order: index
  }));
};

  const ensureRequiredTabs = (tabsArray) => {
    if (!Array.isArray(tabsArray)) tabsArray = [];
    
    const hasBasicInfo = tabsArray.some(tab => tab?.title === 'Basic Info');
    
    let updatedTabs = [...tabsArray];
    
    if (!hasBasicInfo) {
      const defaultTabs = createDefaultRequiredTabs();
      const basicInfoTab = defaultTabs.find(tab => tab.title === 'Basic Info');
      if (basicInfoTab) updatedTabs.push(basicInfoTab);
    }
    
    return enforceRequiredTabPositions(updatedTabs);
  };

  useEffect(() => {
    if (tabs.length > 0) {
      initializeGroupCollapsedStates(tabs);
    }
  }, [tabs, initializeGroupCollapsedStates]);

  useEffect(() => {
    if (!SECTIONS_ENDPOINT || !API_NONCE) {
      const defaultTabs = createDefaultRequiredTabs();
      const enforcedTabs = enforceRequiredTabPositions(defaultTabs);
      setTabs(enforcedTabs);
      setIsLoading(false);
      return;
    }

    const fetchConfiguration = async () => {
      try {
        setIsLoading(true);
        
        const response = await axios.get(SECTIONS_ENDPOINT, AXIOS_CONFIG);
        
        if (response.data && response.data.success) {
          let data = response.data.data || [];
          
          if (data.length === 0) {
            data = createDefaultRequiredTabs();
          } else {
            data = mergeWithDefaultStructure(data);
          }
          
          data = enforceRequiredTabPositions(data);
          setTabs(Array.isArray(data) ? data : []);
        } else {
          const defaultTabs = createDefaultRequiredTabs();
          const enforcedTabs = enforceRequiredTabPositions(defaultTabs);
          setTabs(enforcedTabs);
        }
      } catch (error) {
        const defaultTabs = createDefaultRequiredTabs();
        const enforcedTabs = enforceRequiredTabPositions(defaultTabs);
        setTabs(enforcedTabs);
        
        if (error.response) {
          if (error.response.status === 500) {
            showToast('error', __('Server Error', 'havenlytics'), __('Internal server error. Check PHP error logs.', 'havenlytics'));
          } else if (error.response.status === 403) {
            showToast('error', __('Permission Denied', 'havenlytics'), __('You do not have permission to access this resource.', 'havenlytics'));
          } else {
            showToast('error', __('Load Failed', 'havenlytics'), __('Failed to load configuration:', 'havenlytics') + ` ${error.response.status}`);
          }
        } else if (error.request) {
          showToast('error', __('Network Error', 'havenlytics'), __('No response received from server. Check your connection.', 'havenlytics'));
        } else {
          showToast('error', __('Error', 'havenlytics'), __('An unexpected error occurred.', 'havenlytics'));
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchConfiguration();
  }, [SECTIONS_ENDPOINT, API_NONCE]);

  const saveConfiguration = useCallback(async () => {
    if (!SECTIONS_ENDPOINT || !API_NONCE) {
      showToastRef.current('error', __('Configuration Error', 'havenlytics'), __('Missing API configuration', 'havenlytics'));
      return false;
    }

    setIsSaving(true);
    
    if (!tabsRef.current || tabsRef.current.length === 0) {
      const defaultTabs = createDefaultRequiredTabs();
      const enforcedTabs = enforceRequiredTabPositions(defaultTabs);
      tabsRef.current = enforcedTabs;
      setTabs(enforcedTabs);
    }
    
    const tabsToSave = mergeWithDefaultStructure(tabsRef.current);
    
    try {
      const payload = {
        tabs: tabsToSave.map(tab => ({
          id: tab.id,
          title: tab.title,
          icon: tab.icon,
          fields: (tab.fields || []).map(field => {
            const fieldData = {
              id: field.id,
              name: field.name || field.id,
              type: field.type,
              label: field.label,
              placeholder: field.placeholder || '',
              required: Boolean(field.required),
              adminOnly: Boolean(field.adminOnly),
              enabled: field.enabled !== false,
              locked: Boolean(field.locked),
              order: field.order || 0,
              hidden: Boolean(field.hidden)
            };

            if (field.group_id) {
              fieldData.group_id = field.group_id;
              fieldData.group_type = field.group_type;
              fieldData.group_name = field.group_name;
              fieldData.group_position = field.group_position;
              fieldData.group_total = field.group_total;
              fieldData.group_base_id = field.group_base_id;
              fieldData.metaKey = field.metaKey;
              fieldData.group_collapsed = field.group_collapsed || false;
              
              if (field.group_type === 'property_docs') {
                fieldData.show_in_sidebar = field.show_in_sidebar !== undefined ? field.show_in_sidebar : true;
              }
            }

            if (field.address_field_name) {
              fieldData.address_field_name = field.address_field_name;
            }
            if (field.lat_field_name) {
              fieldData.lat_field_name = field.lat_field_name;
            }
            if (field.lng_field_name) {
              fieldData.lng_field_name = field.lng_field_name;
            }
            if (field.map_field_name) {
              fieldData.map_field_name = field.map_field_name;
            }

            return fieldData;
          }),
          collapsed: Boolean(tab.collapsed),
          order: tab.order || 0,
          required: Boolean(tab.required)
        }))
      };

      const response = await axios.post(SECTIONS_ENDPOINT, payload, AXIOS_CONFIG);
      
      if (response.data && response.data.success) {
        let data = response.data.data || [];
        data = mergeWithDefaultStructure(data);
        data = enforceRequiredTabPositions(data);
        
        setTabs(Array.isArray(data) ? data : []);
        tabsRef.current = Array.isArray(data) ? data : [];
        
        showToastRef.current('success', __('Configuration Saved', 'havenlytics'), __('Property builder configuration saved successfully!', 'havenlytics'));
        return true;
      } else {
        const errorMessage = response.data?.message || __('Save failed - invalid response structure', 'havenlytics');
        throw new Error(errorMessage);
      }
    } catch (error) {
      if (error.response) {
        let errorMessage = __('Failed to save configuration', 'havenlytics');
        if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 400) {
          errorMessage = __('Bad request - check data structure', 'havenlytics');
        } else if (error.response.status === 403) {
          errorMessage = __('Permission denied. Please check your user capabilities.', 'havenlytics');
        } else if (error.response.status === 500) {
          errorMessage = __('Server error. Please check your PHP error logs.', 'havenlytics');
        }
        
        showToastRef.current('error', __('Save Failed', 'havenlytics'), errorMessage);
      } else if (error.request) {
        showToastRef.current('error', __('Network Error', 'havenlytics'), __('No response received from server. Check your connection.', 'havenlytics'));
      } else {
        showToastRef.current('error', __('Save Failed', 'havenlytics'), error.message);
      }
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [SECTIONS_ENDPOINT, API_NONCE]);

  const resetConfiguration = useCallback(async () => {
    if (!SECTIONS_ENDPOINT || !API_NONCE) {
      showToastRef.current('error', __('Configuration Error', 'havenlytics'), __('Missing API configuration', 'havenlytics'));
      return false;
    }
    
    setIsLoading(true);
    
    try {
      const cleanupResponse = await resetAndCleanup();
      
      const response = await axios.delete(SECTIONS_ENDPOINT, AXIOS_CONFIG);
      
      if (response.data && response.data.success) {
        const defaultTabs = createDefaultRequiredTabs();
        const enforcedTabs = enforceRequiredTabPositions(defaultTabs);
        
        const hasDocumentTab = enforcedTabs.some(tab => tab.title === 'Property Documents');
        if (!hasDocumentTab) {
          enforcedTabs.push({
            id: `sec_property_docs_${Date.now()}`,
            title: 'Property Documents',
            icon: 'fas fa-file-pdf',
            fields: [],
            collapsed: false,
            order: enforcedTabs.length,
            required: false
          });
        }
        
        setTabs(enforcedTabs);
        tabsRef.current = enforcedTabs;
        setGroupCollapsedStates({});
        
        if (cleanupResponse && cleanupResponse.success) {
          const message = cleanupResponse.deleted_count 
            ? sprintf(
                __('Configuration reset successfully. Cleaned up %d orphaned entries.', 'havenlytics'),
                cleanupResponse.deleted_count
              )
            : __('Configuration reset successfully.', 'havenlytics');
          
          showToastRef.current('success', __('Configuration Reset', 'havenlytics'), message);
        } else {
          showToastRef.current('success', __('Configuration Reset', 'havenlytics'), __('Configuration reset successfully.', 'havenlytics'));
        }
        
        setShouldReload(true);
        
        return true;
      } else {
        const errorMsg = response.data?.message || __('Reset failed', 'havenlytics');
        throw new Error(errorMsg);
      }
    } catch (error) {
      const defaultTabs = createDefaultRequiredTabs();
      const enforcedTabs = enforceRequiredTabPositions(defaultTabs);
      
      const hasDocumentTab = enforcedTabs.some(tab => tab.title === 'Property Documents');
      if (!hasDocumentTab) {
        enforcedTabs.push({
          id: `sec_property_docs_${Date.now()}`,
          title: 'Property Documents',
          icon: 'fas fa-file-pdf',
          fields: [],
          collapsed: false,
          order: enforcedTabs.length,
          required: false
        });
      }
      
      setTabs(enforcedTabs);
      tabsRef.current = enforcedTabs;
      setGroupCollapsedStates({});
      
      if (error.response?.status !== 404) {
        let errorMessage = __('Failed to reset configuration, but required tabs have been restored.', 'havenlytics');
        showToastRef.current('error', __('Reset Failed', 'havenlytics'), errorMessage);
      } else {
        showToastRef.current('success', __('Configuration Reset', 'havenlytics'), __('Configuration reset successfully.', 'havenlytics'));
      }
      
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [SECTIONS_ENDPOINT, API_NONCE, resetAndCleanup]);

  const deleteGroup = (groupId) => {
    const groupFields = [];
    tabs.forEach(tab => {
      tab.fields.forEach(field => {
        if (field.group_id === groupId) {
          groupFields.push(field);
        }
      });
    });
    
    const fieldNames = groupFields
      .map(f => f.name || f.id || '')
      .filter(name => name);
    
    const groupExists = tabs.some(tab => 
      tab.fields.some(field => field.group_id === groupId)
    );
    
    if (!groupExists) {
      showToast('error', __('Error', 'havenlytics'), __('Group not found', 'havenlytics'));
      return;
    }
    
    window.HvnlyNabAlert.confirm(
      __('Are you sure you want to delete this group and all its fields?', 'havenlytics'),
      __('Delete Group?', 'havenlytics')
    ).then(async (userConfirmed) => {
      if (userConfirmed === 'confirm') {
        setTabs(prevTabs => {
          return prevTabs.map(tab => {
            const newTab = JSON.parse(JSON.stringify(tab));
            const hasGroup = newTab.fields.some(field => field.group_id === groupId);
            
            if (hasGroup) {
              const updatedFields = newTab.fields.filter(field => field.group_id !== groupId);
              const reorderedFields = updatedFields.map((field, index) => ({
                ...field,
                order: index
              }));
              newTab.fields = reorderedFields;
            }
            return newTab;
          });
        });
        
        if (fieldNames.length > 0) {
          await cleanupOrphanedMeta(fieldNames, 0);
        }
        
        setGroupCollapsedStates(prev => {
          const newStates = { ...prev };
          delete newStates[groupId];
          return newStates;
        });
        
        setTimeout(() => {
          setTabs(currentTabs => {
            return [...currentTabs];
          });
          
          showToast('success', __('Group Deleted', 'havenlytics'), 
            sprintf(__('Group and %d fields deleted', 'havenlytics'), fieldNames.length));
        }, 100);
      }
    });
  };

  const addTab = (tabData) => {
    const newTab = { 
      id: `sec_${uuidv4()}`, 
      title: tabData.title || __('New Section', 'havenlytics'),
      icon: tabData.icon || 'fas fa-home',
      fields: [], 
      collapsed: false,
      order: tabs.length,
      required: false
    };
    
    const newTabs = [...tabs, newTab];
    const enforcedTabs = ensureRequiredTabs(newTabs);
    setTabs(enforcedTabs);
    setIsTabModalOpen(false);
    showToast('success', __('Tab Added', 'havenlytics'), __('Tab added successfully', 'havenlytics'));
  };

  const editTab = (tabId, tabData) => {
    const tabToEdit = tabs.find(t => t.id === tabId);
    
    if (isRequiredTab(tabToEdit)) {
      showToast('error', __('Cannot Edit', 'havenlytics'), __('This tab is required and cannot be edited', 'havenlytics'));
      return;
    }
    
    const updatedTabs = tabs.map(tab => 
      tab.id === tabId ? { 
        ...tab, 
        title: tabData.title || tab.title,
        icon: tabData.icon || tab.icon
      } : tab
    );
    
    const enforcedTabs = ensureRequiredTabs(updatedTabs);
    setTabs(enforcedTabs);
    setIsTabModalOpen(false);
    showToast('success', __('Tab Updated', 'havenlytics'), __('Tab updated successfully', 'havenlytics'));
  };

  const deleteTab = (tabId) => {
    const tab = tabs.find(t => t.id === tabId);
    
    if (isRequiredTab(tab)) {
      showToast('error', __('Cannot Delete', 'havenlytics'), __('This tab is required and cannot be deleted', 'havenlytics'));
      return;
    }
    
    window.HvnlyNabAlert.confirm(
      __('Are you sure you want to delete the section', 'havenlytics') + ` "${tab?.title}"?`,
      __('Delete Section?', 'havenlytics')
    ).then((userConfirmed) => {
      if (userConfirmed === 'confirm') {
        const newTabs = tabs.filter(tab => tab.id !== tabId);
        const enforcedTabs = ensureRequiredTabs(newTabs);
        setTabs(enforcedTabs);
        showToast('success', __('Tab Removed', 'havenlytics'), __('Section', 'havenlytics') + ` "${tab?.title}" ` + __('removed successfully', 'havenlytics'));
      }
    });
  };

  const toggleTabCollapse = (tabId) => {
    setTabs(tabs.map(tab => 
      tab.id === tabId ? { ...tab, collapsed: !tab.collapsed } : tab
    ));
  };

  const addField = (tabId, fieldData) => {
    const tab = tabs.find(t => t.id === tabId);
    
    if (isRequiredTab(tab)) {
      showToast('error', __('Cannot Add Field', 'havenlytics'), __('Cannot add fields to required tabs', 'havenlytics'));
      return;
    }
    
    setTabs(prevTabs => {
      return prevTabs.map(tab => {
        if (tab.id === tabId) {
          const newField = { 
            id: `fld_${uuidv4()}`, 
            ...fieldData, 
            enabled: fieldData.enabled !== false,
            order: tab.fields?.length || 0,
            locked: false
          };
          
          return { 
            ...tab, 
            fields: [...(tab.fields || []), newField] 
          };
        }
        return tab;
      });
    });
  };

  const editField = (tabId, fieldId, fieldData) => {
    const tab = tabs.find(t => t.id === tabId);
    const field = tab?.fields?.find(f => f.id === fieldId);
    
    if (field?.locked) {
      showToast('error', __('Cannot Edit', 'havenlytics'), __('This field is required and cannot be edited', 'havenlytics'));
      return;
    }
    
    const newTabs = tabs.map(tab => {
      if (tab.id === tabId) {
        return { 
          ...tab, 
          fields: (tab.fields || []).map(f => 
            f.id === fieldId ? { ...f, ...fieldData } : f
          ) 
        };
      }
      return tab;
    });
    
    setTabs(newTabs);
    setIsFieldModalOpen(false);
    setActiveField(null);
    showToast('success', __('Field Updated', 'havenlytics'), __('Field updated successfully', 'havenlytics'));
  };

  const deleteField = (tabId, fieldId) => {
    const tab = tabs.find(t => t.id === tabId);
    const field = tab?.fields?.find(f => f.id === fieldId);
    
    if (field?.locked) {
      showToast('error', __('Cannot Delete', 'havenlytics'), __('This field is required and cannot be deleted', 'havenlytics'));
      return;
    }
    
    window.HvnlyNabAlert.confirm(
      __('Are you sure you want to delete the field', 'havenlytics') + ` "${field?.label}"?`,
      __('Delete Field?', 'havenlytics')
    ).then(async (userConfirmed) => {
      if (userConfirmed === 'confirm') {
        const fieldName = field?.name || field?.id || '';
        
        setTabs(prevTabs => {
          return prevTabs.map(tab => 
            tab.id === tabId ? { 
              ...tab, 
              fields: (tab.fields || []).filter(f => f.id !== fieldId) 
            } : tab
          );
        });
        
        if (fieldName) {
          await cleanupOrphanedMeta([fieldName], 0);
        }
        
        showToast('success', __('Field Removed', 'havenlytics'), __('Field', 'havenlytics') + ` "${field?.label}" ` + __('removed successfully', 'havenlytics'));
      }
    });
  };

  const toggleField = (tabId, fieldId) => {
    const tab = tabs.find(t => t.id === tabId);
    const field = tab?.fields?.find(f => f.id === fieldId);
    
    if (field?.locked) {
      showToast('error', __('Cannot Modify', 'havenlytics'), __('This field is required and cannot be modified', 'havenlytics'));
      return;
    }
    
    setTabs(tabs.map(tab => {
      if (tab.id === tabId) {
        return { 
          ...tab, 
          fields: (tab.fields || []).map(f => 
            f.id === fieldId ? { ...f, enabled: !f.enabled } : f
          ) 
        };
      }
      return tab;
    }));
  };

  const toggleFieldRequired = (tabId, fieldId) => {
    const tab = tabs.find(t => t.id === tabId);
    const field = tab?.fields?.find(f => f.id === fieldId);
    
    if (field?.locked) {
      showToast('error', __('Cannot Modify', 'havenlytics'), __('This field is required and cannot be modified', 'havenlytics'));
      return;
    }
    
    setTabs(tabs.map(tab => {
      if (tab.id === tabId) {
        return { 
          ...tab, 
          fields: (tab.fields || []).map(f => 
            f.id === fieldId ? { ...f, required: !f.required } : f
          ) 
        };
      }
      return tab;
    }));
  };

  const toggleFieldAdminOnly = (tabId, fieldId) => {
    const tab = tabs.find(t => t.id === tabId);
    const field = tab?.fields?.find(f => f.id === fieldId);
    
    if (field?.locked) {
      showToast('error', __('Cannot Modify', 'havenlytics'), __('This field is required and cannot be modified', 'havenlytics'));
      return;
    }
    
    setTabs(tabs.map(tab => {
      if (tab.id === tabId) {
        return { 
          ...tab, 
          fields: (tab.fields || []).map(f => 
            f.id === fieldId ? { ...f, adminOnly: !f.adminOnly } : f
          ) 
        };
      }
      return tab;
    }));
  };

  const moveFieldUp = (tabId, fieldId) => {
    const tab = tabs.find(t => t.id === tabId);
    const field = tab?.fields?.find(f => f.id === fieldId);
    
    if (field?.locked) {
      showToast('error', __('Cannot Move', 'havenlytics'), __('This field is required and cannot be moved', 'havenlytics'));
      return;
    }
    
    if (field?.group_id) {
      showToast('error', __('Cannot Move', 'havenlytics'), __('Individual group fields cannot be moved. Please move the entire group.', 'havenlytics'));
      return;
    }
    
    setTabs(prevTabs => {
      return prevTabs.map(tab => {
        if (tab.id === tabId) {
          const fields = [...(tab.fields || [])];
          const currentIndex = fields.findIndex(f => f.id === fieldId);
          
          if (currentIndex > 0) {
            if (fields[currentIndex - 1]?.group_id) {
              showToast('error', __('Cannot Move', 'havenlytics'), __('Cannot move field above a group. Move the group instead.', 'havenlytics'));
              return tab;
            }
            
            const newFields = [
              ...fields.slice(0, currentIndex - 1),
              fields[currentIndex],
              fields[currentIndex - 1],
              ...fields.slice(currentIndex + 1)
            ];
            
            const updatedFields = newFields.map((field, index) => ({
              ...field,
              order: index
            }));
            
            return { ...tab, fields: updatedFields };
          }
        }
        return tab;
      });
    });
  };

  const moveFieldDown = (tabId, fieldId) => {
    const tab = tabs.find(t => t.id === tabId);
    const field = tab?.fields?.find(f => f.id === fieldId);
    
    if (field?.locked) {
      showToast('error', __('Cannot Move', 'havenlytics'), __('This field is required and cannot be moved', 'havenlytics'));
      return;
    }
    
    if (field?.group_id) {
      showToast('error', __('Cannot Move', 'havenlytics'), __('Individual group fields cannot be moved. Please move the entire group.', 'havenlytics'));
      return;
    }
    
    setTabs(prevTabs => {
      return prevTabs.map(tab => {
        if (tab.id === tabId) {
          const fields = [...(tab.fields || [])];
          const currentIndex = fields.findIndex(f => f.id === fieldId);
          
          if (currentIndex < fields.length - 1) {
            if (fields[currentIndex + 1]?.group_id) {
              showToast('error', __('Cannot Move', 'havenlytics'), __('Cannot move field below a group. Move the group instead.', 'havenlytics'));
              return tab;
            }
            
            const newFields = [
              ...fields.slice(0, currentIndex),
              fields[currentIndex + 1],
              fields[currentIndex],
              ...fields.slice(currentIndex + 2)
            ];
            
            const updatedFields = newFields.map((field, index) => ({
              ...field,
              order: index
            }));
            
            return { ...tab, fields: updatedFields };
          }
        }
        return tab;
      });
    });
  };

  const openTabModal = (tab = null) => { 
    setActiveTab(tab?.id || null); 
    setActiveField(null); 
    setIsTabModalOpen(true); 
  };

  const openFieldModal = (tabId, field = null) => { 
    setActiveTab(tabId); 
    setActiveField(field); 
    setIsFieldModalOpen(true); 
  };

  return {
    tabs, 
    setTabs,
    activeTab, 
    activeField,
    isTabModalOpen, 
    setIsTabModalOpen,
    isFieldModalOpen, 
    setIsFieldModalOpen,
    isSaving,
    isLoading,
    isCleaning,
    groupCollapsedStates,
    openTabModal, 
    openFieldModal,
    saveConfiguration, 
    resetConfiguration,
    addTab, 
    editTab, 
    deleteTab, 
    toggleTabCollapse,
    addField, 
    editField, 
    deleteField,
    toggleField, 
    toggleFieldRequired, 
    toggleFieldAdminOnly,
    moveFieldUp, 
    moveFieldDown,
    moveGroupUp, 
    moveGroupDown,
    toggleGroupCollapse,
    deleteGroup,
    toggleSidebar,
    isRequiredTab
  };
};